import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { EVENT_DISPLAY_NAME_MAX_LENGTH } from '@shared/constants/textLimits';
import type { WebsiteHeaderEventTitle } from '@shared/types/WebsiteHeaderSettings.types';
import { resolveSafeEventTitleHtml } from '@shared/utils/eventTitleHtml';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';

/**
 * The names edit for a Slider/Marquee shown full bleed for a multi-photo Motion (Showcase, Polaroid).
 * That header lives only in memory, so its names must edit what the Slider edits, exactly as
 * `EventNameNav` sends it: the Slider's own rich title (`editHeaderEventTitle`) when it has one and the
 * multiline-title flag is on, else the event display name (`editText`). Never
 * `editWebsiteHeaderCustomTitle`, which would save the full-bleed header over the couple's Slider and
 * drop every photo but the first.
 */
export const useConvertedHeaderTitleEdit = (sourceTitle: WebsiteHeaderEventTitle | undefined, eventDisplayName: string | null | undefined): (() => void) => {
  const [isMultilineTitleEnabled] = useIsFeatureEnabled('websiteHeaderMultilineTitleEnabled');
  const inlineEditorTransFn = useTranslation('joykit').t('inlineEditor');
  return useEventCallback(() => {
    const titleHtml = resolveSafeEventTitleHtml(sourceTitle, isMultilineTitleEnabled);
    const label = inlineEditorTransFn.displayName();
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: titleHtml
        ? { action: 'editHeaderEventTitle', inlineEditData: { textContent: titleHtml, label } }
        : { action: 'editText', inlineEditData: { textContent: eventDisplayName || '', label, validationConstraints: { maxLength: EVENT_DISPLAY_NAME_MAX_LENGTH } } },
    });
  });
};
